import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import TeacherNav from '../../../components/TeacherNav';
import { OptionalAppShell } from '../../../components/layout/AppShell';
import { PageHeader } from '../../../components/layout/PageHeader';
import { teacherV2NavItems } from '../../../components/layout/v2NavItems';
import { isUiRedesignPageEnabled } from '../../../config/uiRedesign';
import { sectionsApi } from '../../../services/apiV2';
import type { Section } from '../../../types/v2';

type RolloverAction = 'enroll' | 'graduate' | 'skip';

interface RolloverStudent {
  student_id: number;
  first_name: string;
  last_name: string;
  email: string;
  current_grade: string;
  historical_grade: string;
  group_name: string;
  action: RolloverAction;
  target_grade: string;
}

interface RolloverPreview {
  source_section: Section;
  suggested_academic_year: string;
  suggested_target_name: string;
  students: RolloverStudent[];
}

const GRADES = ['6th', '7th', '8th', '9th', '10th', '11th', '12th'];

const normalizeArray = (data: any): Section[] => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

export const V2Rollover: React.FC = () => {
  const redesignEnabled = isUiRedesignPageEnabled('v2_teacher_students');
  const [sections, setSections] = useState<Section[]>([]);
  const [sourceSectionId, setSourceSectionId] = useState<number | null>(null);
  const [preview, setPreview] = useState<RolloverPreview | null>(null);
  const [students, setStudents] = useState<RolloverStudent[]>([]);
  const [targetName, setTargetName] = useState('');
  const [academicYear, setAcademicYear] = useState('');
  const [semester, setSemester] = useState('');
  const [copyPlans, setCopyPlans] = useState(true);
  const [deactivateSource, setDeactivateSource] = useState(true);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<any>(null);

  useEffect(() => {
    const loadSections = async () => {
      setLoading(true);
      try {
        const response = await sectionsApi.list();
        const activeSections = normalizeArray(response.data).filter((section) => section.is_active);
        setSections(activeSections);
        setSourceSectionId(activeSections[0]?.id || null);
      } catch (err: any) {
        setError(err?.response?.data?.error || 'Unable to load sections.');
      } finally {
        setLoading(false);
      }
    };
    loadSections();
  }, []);

  useEffect(() => {
    if (!sourceSectionId) {
      setPreview(null);
      setStudents([]);
      return;
    }
    const loadPreview = async () => {
      setLoading(true);
      setError('');
      setResult(null);
      try {
        const response = await sectionsApi.rolloverPreview(sourceSectionId);
        const data = response.data as RolloverPreview;
        setPreview(data);
        setStudents(data.students || []);
        setTargetName(data.suggested_target_name || '');
        setAcademicYear(data.suggested_academic_year || '');
        setSemester(data.source_section?.semester || '');
      } catch (err: any) {
        setError(err?.response?.data?.error || 'Unable to preview this rollover.');
      } finally {
        setLoading(false);
      }
    };
    loadPreview();
  }, [sourceSectionId]);

  const counts = useMemo(() => students.reduce(
    (totals, student) => {
      totals[student.action] += 1;
      return totals;
    },
    { enroll: 0, graduate: 0, skip: 0 },
  ), [students]);

  const updateStudent = (studentId: number, patch: Partial<RolloverStudent>) => {
    setStudents((current) => current.map((student) => (
      student.student_id === studentId ? { ...student, ...patch } : student
    )));
  };

  const handleActionChange = (student: RolloverStudent, action: RolloverAction) => {
    let targetGrade = student.target_grade;
    if (action === 'enroll' && !GRADES.includes(targetGrade)) {
      targetGrade = GRADES.includes(student.current_grade) ? student.current_grade : '';
    }
    if (action !== 'enroll') targetGrade = '';
    updateStudent(student.student_id, { action, target_grade: targetGrade });
  };

  const applyRollover = async () => {
    if (!sourceSectionId || !targetName.trim() || !academicYear.trim()) return;
    const confirmed = window.confirm(
      `Create "${targetName.trim()}" with ${counts.enroll} enrolled, mark ${counts.graduate} graduated, and keep ${counts.skip} out of the new section?`,
    );
    if (!confirmed) return;

    setApplying(true);
    setError('');
    try {
      const response = await sectionsApi.rollover(sourceSectionId, {
        target_section_name: targetName.trim(),
        academic_year: academicYear.trim(),
        semester: semester.trim(),
        copy_plans: copyPlans,
        deactivate_source: deactivateSource,
        students: students.map((student) => ({
          student_id: student.student_id,
          action: student.action,
          target_grade: student.action === 'enroll' ? student.target_grade : '',
        })),
      });
      setResult(response.data);
    } catch (err: any) {
      const details = err?.response?.data?.details;
      setError(
        Array.isArray(details)
          ? details.join(' ')
          : err?.response?.data?.error || 'The rollover could not be completed.',
      );
    } finally {
      setApplying(false);
    }
  };

  return (
    <OptionalAppShell enabled={redesignEnabled} role="teacher" navItems={teacherV2NavItems('students')}>
      {!redesignEnabled && <TeacherNav activeTab="students" />}
      <div className={redesignEnabled ? 'space-y-5' : 'mx-auto max-w-6xl p-6'}>
        <PageHeader
          eyebrow="Student Management"
          title="New year rollover"
          subtitle="Promote students, record graduates, and create a clean section without deleting last year's work."
          actions={<Link className="rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700" to="/v2/teacher/students">Back to students</Link>}
        />

        {error && (
          <div role="alert" className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {result ? (
          <div className="rounded-lg border border-green-200 bg-green-50 p-6">
            <h3 className="text-lg font-semibold text-green-900">Rollover complete</h3>
            <p className="mt-2 text-sm text-green-800">
              {result.summary.enrolled} enrolled, {result.summary.graduated} graduated, and {result.summary.skipped} skipped.
              {result.summary.plans_reused ? ` ${result.summary.plans_reused} plan assignment${result.summary.plans_reused === 1 ? '' : 's'} reused.` : ''}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link
                className="rounded bg-indigo-600 px-4 py-2 text-sm text-white"
                to={`/v2/teacher/section/${result.target_section.id}`}
              >
                Open {result.target_section.name}
              </Link>
              <Link className="rounded border border-green-300 bg-white px-4 py-2 text-sm text-green-800" to="/v2/teacher/students">
                Return to students
              </Link>
            </div>
          </div>
        ) : (
          <>
            <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
              <h3 className="text-base font-semibold text-gray-900">1. Choose the old section and new year</h3>
              <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <label className="text-sm text-gray-700">
                  Source section
                  <select
                    aria-label="Source section"
                    className="mt-1 block w-full rounded border border-gray-300 px-3 py-2"
                    value={sourceSectionId || ''}
                    onChange={(event) => setSourceSectionId(Number(event.target.value))}
                  >
                    {sections.map((section) => (
                      <option key={section.id} value={section.id}>{section.name}</option>
                    ))}
                  </select>
                </label>
                <label className="text-sm text-gray-700">
                  New section name
                  <input
                    aria-label="New section name"
                    className="mt-1 block w-full rounded border border-gray-300 px-3 py-2"
                    value={targetName}
                    onChange={(event) => setTargetName(event.target.value)}
                  />
                </label>
                <label className="text-sm text-gray-700">
                  Academic year
                  <input
                    aria-label="Academic year"
                    className="mt-1 block w-full rounded border border-gray-300 px-3 py-2"
                    value={academicYear}
                    onChange={(event) => setAcademicYear(event.target.value)}
                    placeholder="2026-27"
                  />
                </label>
                <label className="text-sm text-gray-700">
                  Semester
                  <input
                    aria-label="Semester"
                    className="mt-1 block w-full rounded border border-gray-300 px-3 py-2"
                    value={semester}
                    onChange={(event) => setSemester(event.target.value)}
                    placeholder="Optional"
                  />
                </label>
              </div>
            </section>

            <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-4">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">2. Review every student</h3>
                  <p className="mt-1 text-sm text-gray-500">Recommendations promote grades 6–11 and graduate grade 12. Adjust exceptions before applying.</p>
                </div>
                <div className="flex flex-wrap gap-2 text-sm">
                  <span className="rounded bg-indigo-50 px-3 py-1 text-indigo-700">{counts.enroll} enrolling</span>
                  <span className="rounded bg-green-50 px-3 py-1 text-green-700">{counts.graduate} graduating</span>
                  <span className="rounded bg-gray-100 px-3 py-1 text-gray-700">{counts.skip} not enrolling</span>
                </div>
              </div>

              {loading ? (
                <p className="p-6 text-sm text-gray-500">Preparing rollover preview…</p>
              ) : !preview || students.length === 0 ? (
                <p className="p-6 text-sm text-gray-500">
                  {sections.length === 0 ? 'There are no active sections to roll over.' : 'This section has no active students.'}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-medium uppercase text-gray-500">Student</th>
                        <th className="px-5 py-3 text-left text-xs font-medium uppercase text-gray-500">Current grade</th>
                        <th className="px-5 py-3 text-left text-xs font-medium uppercase text-gray-500">Rollover action</th>
                        <th className="px-5 py-3 text-left text-xs font-medium uppercase text-gray-500">New grade</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {students.map((student) => (
                        <tr key={student.student_id}>
                          <td className="px-5 py-4">
                            <p className="text-sm font-medium text-gray-900">{student.first_name} {student.last_name}</p>
                            <p className="text-xs text-gray-500">{student.email}</p>
                          </td>
                          <td className="px-5 py-4 text-sm text-gray-700">{student.current_grade || 'Not set'}</td>
                          <td className="px-5 py-4">
                            <select
                              aria-label={`Rollover action for ${student.first_name} ${student.last_name}`}
                              className="rounded border border-gray-300 px-3 py-2 text-sm"
                              value={student.action}
                              onChange={(event) => handleActionChange(student, event.target.value as RolloverAction)}
                            >
                              <option value="enroll">Enroll in new section</option>
                              <option value="graduate">Mark graduated</option>
                              <option value="skip">Do not enroll</option>
                            </select>
                          </td>
                          <td className="px-5 py-4">
                            {student.action === 'enroll' ? (
                              <select
                                aria-label={`New grade for ${student.first_name} ${student.last_name}`}
                                className="rounded border border-gray-300 px-3 py-2 text-sm"
                                value={student.target_grade}
                                onChange={(event) => updateStudent(student.student_id, { target_grade: event.target.value })}
                              >
                                <option value="" disabled>Select grade</option>
                                {GRADES.map((grade) => <option key={grade} value={grade}>{grade}</option>)}
                              </select>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
              <h3 className="text-base font-semibold text-gray-900">3. Apply rollover</h3>
              <div className="mt-4 space-y-3">
                <label className="flex items-start gap-3 text-sm text-gray-700">
                  <input type="checkbox" checked={copyPlans} onChange={(event) => setCopyPlans(event.target.checked)} />
                  <span><strong>Reuse active plans.</strong> The new section receives the source section’s active plan assignments.</span>
                </label>
                <label className="flex items-start gap-3 text-sm text-gray-700">
                  <input type="checkbox" checked={deactivateSource} onChange={(event) => setDeactivateSource(event.target.checked)} />
                  <span><strong>Close the old section.</strong> Students stop seeing it, while enrollments and work remain available to teachers.</span>
                </label>
              </div>
              <button
                className="mt-5 rounded bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                disabled={loading || applying || !sourceSectionId || !targetName.trim() || !academicYear.trim()
                  || students.some((student) => student.action === 'enroll' && !GRADES.includes(student.target_grade))}
                onClick={applyRollover}
              >
                {applying ? 'Applying rollover…' : 'Review and apply rollover'}
              </button>
            </section>
          </>
        )}
      </div>
    </OptionalAppShell>
  );
};

export default V2Rollover;
